import { createTheme } from '@mui/material/styles';

/**
 * Admin Dashboard Theme — synced with Android Color.kt
 *
 * Dark Mode:  "Midnight & Gold"  — #0F172A bg, #1E293B surface, #F59E0B primary, #14B8A6 secondary
 * Light Mode: "Ocean & Coral"    — #F8FAFC bg, #FFFFFF surface, #0D9488 primary, #F43F5E secondary
 *
 * Source of truth: resido/app/src/main/java/com/art3mis/resido/ui/theme/Color.kt
 */

export const getTheme = (mode = 'light') => {
  const isDark = mode === 'dark';

  // --- Android Color.kt palette ---
  const colors = {
    dark: {
      primary:          '#F59E0B', // GoldAccent
      primaryLight:     '#FBBF24',
      primaryDark:      '#D97706',
      contrastText:     '#0F172A', // MidnightBlue (dark text on gold)
      secondary:        '#14B8A6', // TealAccent
      secondaryLight:   '#2DD4BF',
      secondaryDark:    '#0F766E',
      background:       '#0F172A', // MidnightBlue
      paper:            '#1E293B', // MidnightSurface
      elevated:         '#334155',
      textPrimary:      'rgba(248,250,252,0.92)', // ~#F8FAFC
      textSecondary:    'rgba(148,163,184,0.8)',   // ~#94A3B8
      textDisabled:     'rgba(148,163,184,0.38)',
      divider:          'rgba(51,65,85,0.6)',      // #334155 at 60%
    },
    light: {
      primary:          '#0D9488', // PrimaryLight (Vibrant Teal)
      primaryLight:     '#14B8A6',
      primaryDark:      '#0F766E',
      contrastText:     '#FFFFFF',
      secondary:        '#F43F5E', // SecondaryLight (Coral/Rose)
      secondaryLight:   '#FB7185',
      secondaryDark:    '#E11D48',
      background:       '#F8FAFC', // BackgroundLight
      paper:            '#FFFFFF', // SurfaceLight
      elevated:         '#FFFFFF',
      textPrimary:      'rgba(30,41,59,0.87)',  // ~OnSurfaceLight #1E293B
      textSecondary:    'rgba(71,85,105,0.65)', // ~#475569
      textDisabled:     'rgba(30,41,59,0.38)',
      divider:          'rgba(226,232,240,0.8)', // #E2E8F0 at 80%
    },
  };

  const c = isDark ? colors.dark : colors.light;

  return createTheme({
    palette: {
      mode,
      primary: {
        main: c.primary,
        light: c.primaryLight,
        dark: c.primaryDark,
        contrastText: c.contrastText,
        // Augmented shades used by Avatars, cards, and selection highlights
        50: isDark ? 'rgba(245,158,11,0.08)' : 'rgba(13,148,136,0.06)',
        100: isDark ? 'rgba(245,158,11,0.12)' : 'rgba(13,148,136,0.10)',
        200: isDark ? 'rgba(245,158,11,0.20)' : 'rgba(13,148,136,0.18)',
      },
      secondary: {
        main: c.secondary,
        light: c.secondaryLight,
        dark: c.secondaryDark,
      },
      success: { main: '#10B981', light: '#34D399', dark: '#059669' },
      warning: { main: '#F59E0B', light: '#FBBF24', dark: '#D97706' },
      error:   { main: '#EF4444', light: '#F87171', dark: '#DC2626' },
      background: {
        default: c.background,
        paper: c.paper,
        elevated: c.elevated,
      },
      text: {
        primary: c.textPrimary,
        secondary: c.textSecondary,
        disabled: c.textDisabled,
      },
      divider: c.divider,
    },
    typography: {
      fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      h1: { fontSize: '1.875rem', fontWeight: 600, letterSpacing: '-0.02em', lineHeight: 1.25 },
      h2: { fontSize: '1.5rem', fontWeight: 600, letterSpacing: '-0.02em', lineHeight: 1.3 },
      h3: { fontSize: '1.25rem', fontWeight: 600, letterSpacing: '-0.01em', lineHeight: 1.35 },
      h4: { fontSize: '1.125rem', fontWeight: 600, letterSpacing: '-0.01em', lineHeight: 1.4 },
      h5: { fontSize: '1rem', fontWeight: 600, lineHeight: 1.45 },
      h6: { fontSize: '0.875rem', fontWeight: 600, lineHeight: 1.5 },
      subtitle1: { fontSize: '0.9375rem', fontWeight: 500, lineHeight: 1.5 },
      subtitle2: { fontSize: '0.8125rem', fontWeight: 500, lineHeight: 1.5 },
      body1: { fontSize: '0.875rem', fontWeight: 400, lineHeight: 1.6 },
      body2: { fontSize: '0.8125rem', fontWeight: 400, lineHeight: 1.6 },
      button: { fontSize: '0.8125rem', fontWeight: 500, textTransform: 'none', letterSpacing: '0' },
      caption: { fontSize: '0.75rem', fontWeight: 400, lineHeight: 1.5, color: c.textSecondary },
      overline: { fontSize: '0.6875rem', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase' },
    },
    shape: { borderRadius: 6 },
    shadows: [
      'none',
      isDark ? '0 1px 2px rgba(0,0,0,0.4)'  : '0 1px 2px rgba(0,0,0,0.06)',
      isDark ? '0 1px 3px rgba(0,0,0,0.5)'  : '0 1px 3px rgba(0,0,0,0.08)',
      isDark ? '0 4px 8px rgba(0,0,0,0.5)'  : '0 4px 8px -2px rgba(0,0,0,0.1)',
      isDark ? '0 6px 16px rgba(0,0,0,0.5)' : '0 6px 16px -4px rgba(0,0,0,0.12)',
      isDark ? '0 12px 24px rgba(0,0,0,0.5)': '0 12px 24px -8px rgba(0,0,0,0.15)',
      ...Array(19).fill('none'),
    ],
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          '*': { boxSizing: 'border-box' },
          body: {
            scrollbarWidth: 'thin',
            scrollbarColor: isDark ? '#334155 transparent' : '#d4d4d4 transparent',
            '&::-webkit-scrollbar': { width: 8, height: 8 },
            '&::-webkit-scrollbar-track': { background: 'transparent' },
            '&::-webkit-scrollbar-thumb': {
              background: isDark ? '#334155' : '#d4d4d4',
              borderRadius: 4,
              border: '2px solid transparent',
              backgroundClip: 'content-box',
            },
            '&::-webkit-scrollbar-thumb:hover': { background: isDark ? '#475569' : '#a3a3a3' },
          },
        },
      },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: {
            borderRadius: 6,
            padding: '6px 12px',
            minHeight: 32,
            fontWeight: 500,
            transition: 'all 0.15s ease',
          },
          sizeSmall: { padding: '4px 10px', fontSize: '0.75rem', minHeight: 28 },
          sizeLarge: { padding: '10px 20px', fontSize: '0.875rem', minHeight: 40 },
          contained: {
            backgroundColor: c.primary,
            color: c.contrastText,
            '&:hover': { backgroundColor: isDark ? '#D97706' : '#0F766E' },
          },
          outlined: {
            borderColor: isDark ? 'rgba(245,158,11,0.4)' : 'rgba(13,148,136,0.4)',
            color: c.primary,
            '&:hover': {
              borderColor: c.primary,
              backgroundColor: isDark ? 'rgba(245,158,11,0.08)' : 'rgba(13,148,136,0.08)',
            },
          },
          text: {
            '&:hover': { backgroundColor: isDark ? 'rgba(245,158,11,0.08)' : 'rgba(13,148,136,0.08)' },
          },
        },
      },
      MuiCard: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: {
            borderRadius: 8,
            border: `1px solid ${c.divider}`,
            backgroundColor: c.paper,
            backgroundImage: 'none',
          },
        },
      },
      MuiPaper: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: { backgroundImage: 'none' },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderColor: c.divider,
            padding: '10px 16px',
            fontSize: '0.8125rem',
          },
          head: {
            backgroundColor: isDark ? 'rgba(15,23,42,0.5)' : 'rgba(0,0,0,0.02)',
            fontWeight: 500,
            fontSize: '0.75rem',
            color: c.textSecondary,
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
            borderBottom: `1px solid ${c.divider}`,
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            transition: 'background-color 0.1s ease',
            '&:hover': { backgroundColor: isDark ? 'rgba(30,41,59,0.5)' : 'rgba(0,0,0,0.02)' },
            '&:last-child td': { borderBottom: 0 },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: { fontWeight: 500, fontSize: '0.75rem', height: 24, borderRadius: 6 },
          sizeSmall: { height: 20, fontSize: '0.6875rem' },
        },
      },
      MuiTextField: {
        defaultProps: { size: 'small' },
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: 6,
              fontSize: '0.8125rem',
              '& fieldset': { borderColor: isDark ? 'rgba(51,65,85,0.6)' : 'rgba(0,0,0,0.12)' },
              '&:hover fieldset': { borderColor: isDark ? 'rgba(51,65,85,0.9)' : 'rgba(0,0,0,0.2)' },
              '&.Mui-focused fieldset': { borderColor: c.primary, borderWidth: 1.5 },
            },
          },
        },
      },
      MuiSelect: {
        defaultProps: { size: 'small' },
        styleOverrides: {
          root: { borderRadius: 6, fontSize: '0.8125rem' },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            border: 'none',
            backgroundColor: isDark ? '#0F172A' : '#F8FAFC',
            borderRight: `1px solid ${c.divider}`,
          },
        },
      },
      MuiAppBar: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: {
            backgroundColor: isDark ? '#0F172A' : '#FFFFFF',
            borderBottom: `1px solid ${c.divider}`,
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            borderRadius: 6,
            padding: 6,
            '&:hover': { backgroundColor: isDark ? 'rgba(245,158,11,0.08)' : 'rgba(13,148,136,0.08)' },
          },
          sizeSmall: { padding: 4 },
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            borderRadius: 6,
            margin: '1px 0',
            padding: '6px 12px',
            transition: 'background-color 0.1s ease',
            '&.Mui-selected': {
              backgroundColor: isDark ? 'rgba(245,158,11,0.12)' : 'rgba(13,148,136,0.08)',
              '&:hover': { backgroundColor: isDark ? 'rgba(245,158,11,0.16)' : 'rgba(13,148,136,0.12)' },
            },
            '&:hover': { backgroundColor: isDark ? 'rgba(30,41,59,0.6)' : 'rgba(0,0,0,0.04)' },
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: isDark ? '#334155' : '#1E293B',
            color: '#F8FAFC',
            fontSize: '0.75rem',
            fontWeight: 500,
            borderRadius: 4,
            padding: '4px 8px',
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: 12,
            border: `1px solid ${c.divider}`,
            boxShadow: isDark ? '0 24px 48px rgba(0,0,0,0.5)' : '0 24px 48px rgba(0,0,0,0.15)',
          },
        },
      },
      MuiAvatar: {
        styleOverrides: {
          root: {
            backgroundColor: isDark ? 'rgba(245,158,11,0.15)' : 'rgba(13,148,136,0.12)',
            color: isDark ? '#F59E0B' : '#0D9488',
            fontWeight: 500,
            fontSize: '0.8125rem',
          },
        },
      },
      MuiTabs: {
        styleOverrides: {
          indicator: { height: 2, borderRadius: 1 },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            fontWeight: 500,
            fontSize: '0.8125rem',
            textTransform: 'none',
            minHeight: 40,
            padding: '8px 16px',
          },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            borderRadius: 8,
            border: `1px solid ${c.divider}`,
            boxShadow: isDark ? '0 8px 24px rgba(0,0,0,0.4)' : '0 8px 24px rgba(0,0,0,0.12)',
            marginTop: 4,
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            fontSize: '0.8125rem',
            padding: '8px 12px',
            borderRadius: 4,
            margin: '2px 4px',
            '&:hover': { backgroundColor: isDark ? 'rgba(30,41,59,0.6)' : 'rgba(0,0,0,0.04)' },
          },
        },
      },
    },
  });
};

const theme = getTheme('light');
export default theme;